'use client';

import { CaretDownIcon } from '@phosphor-icons/react';

import {
  Badge,
  BadgeDot,
  MENU_ACTIVE_ROW_CLASS,
  MOBILE_MENU_ROW_CLASS,
  MOBILE_NESTED_MENU_ROW_CLASS,
} from '../../lib/header-constants';
import { getMegaMenuGroupDotColor, getMegaMenuLinkBadgeColor } from '../../lib/mega-menu-badge';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext } from './context';
import type { NavItem, NavMegaMenuGroup } from '../../types/navigation';

interface HeaderMobileMegaSectionProps {
  expandedGroupIds: string[];
  item: NavItem;
  onClose: () => void;
  onToggleGroup: (groupId: string) => void;
}

interface MobileMegaGroupLinksProps {
  group: NavMegaMenuGroup;
  onClose: () => void;
  onNavClick?: (item: { href: string; label: string; url: string }) => void;
}

const getMobileMegaGroupId = (itemLabel: string, groupId: string) =>
  `mobile-mega-group-${itemLabel}-${groupId}`;

const MobileMegaGroupLinks = ({ group, onClose, onNavClick }: MobileMegaGroupLinksProps) => (
  <ul className="border-adipa-border space-y-0.5 border-l pl-3">
    {group.links.map((link) => (
      <li key={link.label}>
        <a
          className={cn(MOBILE_NESTED_MENU_ROW_CLASS, 'gap-2')}
          href={link.href}
          onClick={() => {
            onNavClick?.({ href: link.href, label: link.label, url: link.href });
            onClose();
          }}
        >
          <span>{link.label}</span>
          {link.badge ? <Badge color={getMegaMenuLinkBadgeColor(link)} label={link.badge} /> : null}
        </a>
      </li>
    ))}

    {group.secondaryAction ? (
      <li>
        <a
          className={cn(MOBILE_NESTED_MENU_ROW_CLASS, MENU_ACTIVE_ROW_CLASS)}
          href={group.secondaryAction.href}
          onClick={() => {
            onNavClick?.({
              href: group.secondaryAction!.href,
              label: group.secondaryAction!.label,
              url: group.secondaryAction!.href,
            });
            onClose();
          }}
        >
          {group.secondaryAction.label}
        </a>
      </li>
    ) : null}
  </ul>
);

const HeaderMobileMegaSection = ({
  expandedGroupIds,
  item,
  onClose,
  onToggleGroup,
}: HeaderMobileMegaSectionProps) => {
  const { onNavClick } = useNavbarContext();

  if (!item.megaMenu) {
    return null;
  }

  return (
    <div className="pt-1">
      <ul className="space-y-0.5">
        {item.megaMenu.categories.map((category) => {
          if (!category.sectionId) {
            return category.href ? (
              <li key={category.label}>
                <a
                  className={MOBILE_MENU_ROW_CLASS}
                  href={category.href}
                  onClick={() => {
                    onNavClick?.({
                      href: category.href!,
                      label: category.label,
                      url: category.href!,
                    });
                    onClose();
                  }}
                >
                  {category.label}
                </a>
              </li>
            ) : null;
          }

          const group = item.megaMenu?.groups.find(
            (candidate) => candidate.id === category.sectionId,
          );

          if (!group) {
            return null;
          }

          const isExpanded = expandedGroupIds.includes(group.id);
          const groupPanelId = getMobileMegaGroupId(item.label, group.id);
          const dotColor = getMegaMenuGroupDotColor(group);

          return (
            <li key={category.label}>
              <button
                aria-controls={groupPanelId}
                aria-expanded={isExpanded}
                className={cn(
                  MOBILE_MENU_ROW_CLASS,
                  'justify-between',
                  isExpanded && MENU_ACTIVE_ROW_CLASS,
                )}
                type="button"
                onClick={() => onToggleGroup(group.id)}
              >
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  {category.label}
                  {dotColor ? <BadgeDot color={dotColor} /> : null}
                </span>
                <CaretDownIcon
                  className={cn(
                    'duration-adipa-fast ml-2 shrink-0 transition-transform ease-out motion-reduce:transition-none',
                    isExpanded && 'rotate-180',
                  )}
                  size={14}
                  weight="bold"
                />
              </button>

              <div
                className={cn(
                  'mobile-accordion-panel',
                  isExpanded ? 'mobile-accordion-panel-open' : 'mobile-accordion-panel-closed',
                )}
                id={groupPanelId}
              >
                <div className="min-h-0 pb-2 pl-3 pr-2 pt-1">
                  <MobileMegaGroupLinks group={group} onClose={onClose} onNavClick={onNavClick} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default HeaderMobileMegaSection;
